import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './ServiceDetail.css';

const ServiceDetail = ({ serviceData, serviceImage, serviceKey }) => {
  const { t } = useTranslation();
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  // Determine icon and color based on service key (language-independent)
  const getServiceIcon = () => {
    switch (serviceKey) {
      case 'mdgImplementation':
        return { text: 'MDG', color: '#36c8ff' };
      case 'dataQuality':
        return { text: 'DQM', color: '#8b5cf6' };
      case 'dataMigration':
        return { text: 'DM', color: '#10b981' };
      case 'strategicConsulting':
        return { text: 'ST', color: '#ea580c' };
      default:
        return { text: 'MDG', color: '#36c8ff' };
    }
  };

  const serviceIcon = getServiceIcon();

  return (
    <div className="service-detail-page">
      {/* Black Header Section with Neon Effects */}
      <section className="service-page-header">
        <div className="service-header-background">
          <div className="glow-orb glow-orb-1"></div>
          <div className="glow-orb glow-orb-2"></div>
          <div className="glow-orb glow-orb-3"></div>
        </div>
        <div className="service-header-container">
          <div className="service-header-content">
            <div className="service-header-title-row">
              <div className="service-header-icon">
                <div className="service-icon service-icon-1" style={{ 
                  backgroundColor: serviceIcon.color
                }}>
                  {serviceIcon.text}
                </div>
              </div>
              <h1>{serviceData.title}</h1>
            </div>
            <p className="service-intro">{serviceData.intro}</p>
          </div>
          <div className="service-header-ctas">
            <Link to="/contact" className="btn btn-primary">{t('servicePage.contactUs')}</Link>
          </div>
        </div>
      </section>

      <div className="service-detail-container">
        <div className="service-main-content">
          <div className="service-description">
            {serviceImage && (
              <div className="service-image-above-overview">
                <img 
                  src={serviceImage} 
                  alt={serviceData.title} 
                  loading="lazy"
                  decoding="async"
                />
              </div>
            )}
            <h2>{t('servicePage.overview')}</h2>
            <p>{serviceData.overview}</p>
            
            <h2>{t('servicePage.keyFeatures')}</h2>
            <ul className="features-list">
              {serviceData.features && serviceData.features.map((feature, index) => (
                <li key={index}>{feature}</li>
              ))}
            </ul>

            <h2>{t('servicePage.implementationProcess')}</h2>
            <p>{serviceData.process}</p>

            <h2>{t('servicePage.benefits')}</h2>
            <ul className="benefits-list">
              {serviceData.benefits && serviceData.benefits.map((benefit, index) => (
                <li key={index}>{benefit}</li>
              ))}
            </ul>

            {serviceData.faq && serviceData.faq.length > 0 && (
              <div className="service-faq-section">
                <h2>{t('servicePage.faq')}</h2>
                <div className="faq-list">
                  {serviceData.faq.map((item, index) => (
                    <div key={index} className="faq-item">
                      <button
                        className="faq-question"
                        onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                        aria-expanded={openFaqIndex === index}
                      >
                        <span className="faq-question-text">{item.question}</span>
                        <span className="faq-icon">{openFaqIndex === index ? '−' : '+'}</span>
                      </button>
                      <div className={`faq-answer ${openFaqIndex === index ? 'faq-answer-open' : ''}`}>
                        <div>
                          <p>{item.answer}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="service-sidebar">
          <div className="sidebar-section">
            <h3>{t('servicePage.serviceDetails')}</h3>
            <div className="detail-item">
              <span className="detail-label">{t('servicePage.type')}</span>
              <span className="detail-value">{serviceData.details.type}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">{t('servicePage.licenseType')}</span>
              <span className="detail-value">{serviceData.details.licenseType}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">{t('servicePage.categories')}</span>
              <span className="detail-value">{serviceData.details.categories}</span>
            </div>
          </div>

          <div className="sidebar-section">
            <h3>{t('servicePage.techRequirements')}</h3>
            <div className="requirements">
              {serviceData.requirements && serviceData.requirements.map((req, index) => (
                <div key={index} className="requirement-item">
                  <strong>{req.name}:</strong> {req.value}
                </div>
              ))}
            </div>
          </div>

          <div className="sidebar-section">
            <h3>{t('servicePage.getStarted')}</h3>
            <p>{t('servicePage.readyToImplement')}</p>
            <Link to="/contact" className="sidebar-cta">{t('servicePage.contactUs')}</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceDetail;
